import { getCurrentUserHighestRole } from '../auth/keycloak';
import type { NotificationItem } from '../types/notification';

/** Highest role the signed-in user holds, as resolved by the app shell. */
export type DeepLinkRole = 'ADMIN' | 'TEACHER' | 'STUDENT' | 'UNKNOWN';

/** Where a notification row should navigate, if anywhere. */
export interface NotificationOpenTarget {
  path: string;
  /** Announcement id / notification id to highlight on the target page. */
  highlight?: string;
}

const CREATE_ROLE_FALLBACK: Record<string, string> = {
  ADMIN: 'ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
};

/**
 * Resolves the role the inbox should route for. Falls back to the cached shell
 * role (`localStorage.role`, written by AppLayout) so behaviour is correct even
 * if a caller forgets to pass the role in.
 *
 * The other components (notification list and bell popover) prefer the live auth
 * session via `resolveDeepLinkRoleFromAuth`, because `localStorage.role` can lag
 * behind a fresh login. This function is kept as the explicit/cached variant the
 * page and its tests call.
 */
export function resolveDeepLinkRole(explicit?: string | null): DeepLinkRole {
  const fromProps = normalizeRole(explicit);
  if (fromProps) return fromProps;
  if (typeof localStorage === 'undefined') return 'UNKNOWN';
  try {
    return normalizeRole(localStorage.getItem('role')) ?? 'UNKNOWN';
  } catch {
    return 'UNKNOWN';
  }
}

/**
 * Resolve the routing role from the live auth session, falling back to the
 * cached `localStorage.role` the shell writes. Used wherever the caller has no
 * explicit role (the bell renders outside the inbox page).
 */
export function resolveDeepLinkRoleFromAuth(): DeepLinkRole {
  const fromSession = normalizeRole(getCurrentUserHighestRole());
  if (fromSession) return fromSession;
  return resolveDeepLinkRole();
}

/**
 * Where a row should navigate, resolved for the caller's role. Shared by the
 * inbox page and the bell popover so both obey the same deep-link contract, and
 * `null` when the row no longer has an openable target.
 */
export function resolveRowOpenTarget(item: NotificationItem): NotificationOpenTarget | null {
  return resolveNotificationTarget(item, resolveDeepLinkRoleFromAuth());
}

function normalizeRole(role?: string | null): DeepLinkRole | null {
  if (!role) return null;
  const upper = role.replace(/^ROLE_/, '').toUpperCase();
  return upper in CREATE_ROLE_FALLBACK ? (upper as DeepLinkRole) : null;
}

/**
 * Picks where a notification opens. Never routes to an unavailable target: a
 * withdrawn announcement, a missing deep link, or an inaccessible classroom
 * yields `null` so the caller can surface "no longer available" instead.
 *
 * The item type decides the route, not the row's `href` alone: the backend's
 * rendered href hardcodes the student submission route for every SUBMISSION row,
 * which is wrong for teachers.
 */
export function resolveNotificationTarget(
  item: NotificationItem,
  role: DeepLinkRole,
): NotificationOpenTarget | null {
  if (!item.targetAvailable) return null;
  if (item.type === 'TEACHER_ANNOUNCEMENT') return resolveAnnouncementTarget(item);

  const href = item.href;
  if (!href) return null;

  if (role === 'TEACHER' || role === 'ADMIN') {
    const reRoute = isSubmissionKind(item.type) || isStudentOwnedLink(href);
    const path = reRoute ? toTeacherPath(href, item.classroomCode) : href;
    return path ? { path, highlight: item.id } : null;
  }
  return { path: href, highlight: item.id };
}

/**
 * Kinds whose server-rendered `href` is meaningful for the student who owns the
 * row (the submission they must review) and must be kept verbatim for them.
 */
function isStudentOwnedLink(href: string): boolean {
  return /^\/student\/assignments\/[^/?#]+\/submission(?:\?|$)/.test(href);
}

/**
 * The submission kinds: the server renders the student submission route for
 * every one of them, so a teacher/admin copy has to be re-routed to the
 * classroom-scoped assignment page they can actually open.
 */
function isSubmissionKind(type: NotificationItem['type']): boolean {
  return (
    type === 'STUDENT_SUBMITTED' ||
    type === 'SUBMISSION_REVIEWED' ||
    type === 'SUBMISSION_COMMENT'
  );
}

/**
 * Announcements live on the classroom announcements tab, so the row opens
 * `?tab=announcements&announcement=<id>` and the panel opens that detail.
 */
function resolveAnnouncementTarget(item: NotificationItem): NotificationOpenTarget | null {
  const classroomCode = item.classroomCode;
  const announcementId = item.announcementId ?? null;
  if (!classroomCode || !announcementId) return null;
  const params = new URLSearchParams({ tab: 'announcements', announcement: announcementId });
  return {
    path: `/classrooms/${encodeURIComponent(classroomCode)}?${params.toString()}`,
    highlight: announcementId,
  };
}

/**
 * Maps a student-shaped link onto the classroom-scoped teacher page. The teacher
 * routes are matched by `ClassroomRouteGuard` for both layouts, so no role guard
 * has to be duplicated here. Returns null when no safe teacher target exists —
 * opening the student page for a teacher would be worse than not opening at all.
 */
function toTeacherPath(href: string, classroomCode: string | null): string | null {
  // Manager practice links already use the registered assignment/student route.
  if (/^\/assignments\/[^/?#]+\/students\/[^/?#]+\/practice(?:[?#]|$)/.test(href)) {
    return href;
  }
  if (isStudentOwnedLink(href)) {
    return classroomCode
      ? `/classrooms/${encodeURIComponent(classroomCode)}?tab=assignments`
      : null;
  }
  return href.startsWith('/classrooms/') || href.startsWith('/assignments/') ? href : null;
}
